import { Currency, CurrencySettings, ModelRow, StringUtil, UrlUtil } from "@mcleod/core";
import {
    BlurEvent, ChangeEvent, Component, DataDisplayEvent, DataHeader, DataSourceExecutionEvent, DataSourceMode,
    DataSourceModeChangeEvent, Label, Layout, LookupModelSearchEvent, LookupModelSelectionEvent, MouseEvent, Tab, Table,
    TableContentsChangedEvent, TableRow, TableRowCreationEvent, Textbox, TableRowDisplayEvent
} from "@mcleod/components";
import { LMEUserSettings } from "@mcleod/general";
import { AutogenLayoutCarrierRate } from "./autogen/AutogenLayoutCarrierRate";
import { LinkedModel } from "@mcleod/core/src/ModelRow";
import { CustomerFscQuickInfo } from "@mcleod/ar";

export class CarrierRate extends AutogenLayoutCarrierRate {

    onLoad(): void | Promise<void> {
        this.layoutTableComment.displayCopyOrderFields = false;
        this.styleTab(this.tabPayAdjustments);
        this.styleTab(this.tabComments);
        this.layoutTableComment.tableComments.addContentsChangedListener((event: TableContentsChangedEvent) => {
            this.tableCommentOnContentsChanged(event)
        });
        this.layoutTableComment.tableComments.addRowCreateListener((event: TableRowCreationEvent) => {
            this.tableCommentOnRowCreate(event)
        });
        this.textboxFuelSurchargePay.quickInfoOverrideSearchMethod = CustomerFscQuickInfo.fuelSurchargePaySearchMethod;

        const result = UrlUtil.getPropsFromUrl(window.location.href);
        if (result?.laneRateId) {
            this.addLayoutDataLoadListener(() => {
                const row = this.layoutCarrierLaneRate.tableCarrierLaneRates.rows.find(row => row.data.get('id') === result.laneRateId);
                if (row) {
                    row.showEditLayout();
                }
            });
        }
    }

    public override doAfterDataHeaderSetup(dataHeader: DataHeader): void {
        dataHeader.promptToSaveChanges();
        dataHeader.showDelete = true;
    }

    textboxOrderTypeBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.is_active = 'Y';
    }

    sourceCarrierRateAfterModeChange(event: DataSourceModeChangeEvent) {
        const isSearch = event.dataSource.mode === DataSourceMode.SEARCH;
        this.layoutCarrierLaneRate.buttonAdd.visible = !isSearch;
        this.layoutCarrierLaneRate.buttonSearch.visible = isSearch;
        this.textboxRateNumber.visible = isSearch;
        if (event.oldMode === DataSourceMode.ADD && event.newMode === DataSourceMode.UPDATE) {
            const childLinks: LinkedModel[] = this.sourceCarrierRate.activeRow?.data["_child_links"];
            childLinks?.forEach(linkedModel => linkedModel.rows?.forEach(row => row._appending = false));
        }
    }

    styleTab(tab: Tab) {
        if (tab.heading != null && tab.headingLabel != null) {
            tab.heading.color = "subtle.light";
            tab.headingLabel.setProps({ color: "default", fontBold: true, fillRow: false })
            if (tab["countLabel"] == null) {
                tab["countLabel"] = new Label({
                    fontBold: true, text: "0", fillRow: true,
                    color: "primary", rowBreak: false, marginLeft: 5
                });
                tab.heading.insert(tab["countLabel"], 1);
            }
        }
    }

    updateTabCountLabel(tab: Tab, table: Table) {
        if (tab != null && tab["countLabel"] != null) {
            tab["countLabel"].text = table.dataSource.rowCount + "";
        }
    }

    tableCommentOnContentsChanged(event: TableContentsChangedEvent) {
        this.updateTabCountLabel(this.tabComments, event.getTable());
    }

    tableCommentOnRowCreate(event: TableRowCreationEvent) {
        this.updateTabCountLabel(this.tabComments, event.getTable());
    }

    tableExtraPayOnContentsChanged(event: TableContentsChangedEvent) {
        this.updateTabCountLabel(this.tabPayAdjustments, event.getTable());
    }

    textboxFuelSurchargePayBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.surcharge_type = 'R';
    }

    textboxExtraStopPayDeductCodeOnChange(event: ChangeEvent) {
        if (StringUtil.isEmptyString(event.newValue)) {
            this.activeRow.set('deduct_code_descr', null);
        }
    }

    textboxExtraStopPayDeductCodeOnLookupModelSelection(event: LookupModelSelectionEvent) {
        const deductCodeRow = event.selection;
        if (deductCodeRow && !deductCodeRow.isNull('short_desc')) {
            this.activeRow.set('deduct_code_descr', deductCodeRow.get('short_desc'));
        }
    }

    tableExtraPayOnRowCreate(event: TableRowCreationEvent) {
        this.updateTabCountLabel(this.tabPayAdjustments, event.getTable());
        const modelRow = event.getTableRow().data as ModelRow;
        if (modelRow._appending) {
            modelRow.set("entered_user_id", LMEUserSettings.getUserId());
        }
    }

    textboxDeductCodeIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.dispatcher_flag = 'Y';
    }

    textboxDeductCodeIdOnChange(event: ChangeEvent) {
        const textbox = event.target as Textbox;
        if (event.userInitiatedChange && event.newValue != event.oldValue) {
            const tableRow = TableRow.getContainingTableRow(textbox);
            const textboxShortDesc = tableRow.findComponentById("textboxShortDesc") as Textbox;
            const selectingFromDropdown = textbox.isDropdownVisible() && textbox.getFirstLookupModelData() != null;
            if (selectingFromDropdown) {
                // after selecting a deduct code, set the textboxShortDesc.text and then textboxUnits gets focus
                textboxShortDesc.text = textbox.getFirstLookupModelData().get("short_desc");
                textbox.hideDropdown(false);
                this.payAdjustmentsTextboxChanged(textbox);
                tableRow.findComponentById("textboxUnits").focus();
            } else {
                textboxShortDesc.text = null;
            }
        }
    }

    textboxUnitsOnBlur(event: BlurEvent) {
        this.payAdjustmentsTextboxOnBlur(event);
    }

    textboxRateOnBlur(event: BlurEvent) {
        this.payAdjustmentsTextboxOnBlur(event);
    }

    payAdjustmentsTextboxOnBlur(event: BlurEvent) {
        if (event.changedWhileFocused) {
            const textbox: Textbox = event.target as Textbox;
            this.payAdjustmentsTextboxChanged(textbox);
        }
    }

    payAdjustmentsTextboxChanged(textbox: Textbox) {
        const tableRow: TableRow = TableRow.getContainingTableRow(textbox);
        const rate = tableRow.data.get("rate");
        const units = tableRow.data.get("units");
        if (!isNaN(rate * units)) {
            tableRow.data.set("amount", new Currency({
                amount: rate * units,
                currency_code: CurrencySettings.getFunctionalCurrency()
            }));
        } else {
            tableRow.data.set("amount", null);
        }
        const amountTextbox: Textbox = tableRow.findComponentById('textboxAmount') as Textbox;
        if (amountTextbox) {
            amountTextbox.displayData(tableRow.data, null, 0);
        }
        if (StringUtil.isEmptyString(textbox.text)) {
            const descTextbox: Textbox = tableRow.findComponentById("textboxShortDesc") as Textbox;
            tableRow.data.set("short_desc", "");
            descTextbox.displayData(tableRow.data, null, 0);
        }
    }

    sourceCommentsAfterExecution() {
        this.updateTabCountLabel(this.tabComments, this.layoutTableComment.tableComments);
    }

    sourceCarrierPayAdjustmentsAfterExecution() {
        this.updateTabCountLabel(this.tabPayAdjustments, this.tableExtraPay);
    }

    sourceCarrierRateAfterExecution(event: DataSourceExecutionEvent) {
        const carrierRate = event.data;
        this.layoutCarrierLaneRate.carrierRateId = carrierRate?.getString('id', null);
        this.layoutCarrierLaneRate.carrierId = carrierRate?.getString('carrier_id', null);
    }

    textboxCarrierOnBlur() {
        if (this.textboxCarrier.isEmpty()) {
            this.layoutCarrierLaneRate.carrierId = null;
        }
    }

    textboxCarrierOnLookupModelSelection(event: LookupModelSelectionEvent) {
        this.layoutCarrierLaneRate.carrierId = event.getValueFromSelectedRow('id');
    }


    tableExtraPayOnRowDisplay(event: TableRowDisplayEvent) {
        const textboxAmount = event.getTableRow()?.findComponentById("textboxAmount") as Textbox;
        textboxAmount.quickInfoOverrideKeyValue = () => {
            const row = TableRow.getContainingTableRow(event.target as Component).data;
            if (row.get("fuel_surcharge_pay") != null) {
                return row.get("id");
            }
            return null;
        };
    }
}
